import { getGatewaySession } from '@/lib/gateway-fetch';
import type { CaseSummary } from './cors-provenance-api';
import { parseCaseCsamFlag, type CaseCsamFlag } from './case-csam-flag';
import { createUserCase, EdgeLambdaApiError } from './cors-edge-lambda-api';
import { isWafBlockBody, WafBlockedError } from './waf-block';

/**
 * Access-aware case list from permission-service, via the Next proxy route
 * ``/api/protected/cases/access`` (-> ``/permission-service/v1/tenants/{tenantId}/user/cases``).
 *
 * Mapped to the same {@link CaseSummary} shape the provenance path returns so
 * ``useCasesList`` consumers are identical regardless of source.
 *
 * Each row keeps the three identifiers permission-service returns: ``name``
 * (display), ``tag`` (provenance / timeline filter), and ``folder_segment``
 * (S3 folder, original casing). Do not re-derive the tag or the folder from
 * ``name`` — that guess matches zero provenance rows and writes the wrong S3
 * prefix once TP-3698 mints UUID folders (TR-359; Case-Management-v2 addendum
 * 2026-07-28). Rows missing a tag or a folder segment are dropped.
 */
interface PermissionCaseDTO {
  name?: unknown;
  tag?: unknown;
  folder_segment?: unknown;
  csam_flagged?: unknown;
  csam_flag_source?: unknown;
  csam_can_turn_off?: unknown;
}

export async function fetchPermissionedCasesList(
  args: { signal?: AbortSignal } = {},
): Promise<CaseSummary[]> {
  const res = await fetch('/api/protected/cases/access', {
    method: 'GET',
    headers: { 'Content-Type': 'application/json' },
    signal: args.signal,
  });

  if (!res.ok) {
    const detail = await res.text().catch(() => '');
    if (isWafBlockBody(res.status, detail)) {
      throw new WafBlockedError('permission-service cases/access was blocked by the edge WAF.', detail);
    }
    throw new Error(
      `permission-service cases/access returned ${res.status}${detail ? `: ${detail}` : ''}`,
    );
  }

  const body = (await res.json().catch(() => null)) as { response?: unknown } | null;
  // Mirror the provenance path (fetchCasesList): a missing/mis-shapen payload is
  // an error, not "no cases", so the UI shows an error state instead of an empty
  // list that looks like the user has access to nothing.
  if (!body || !Array.isArray(body.response)) {
    throw new Error('permission-service cases/access returned a non-array body.');
  }

  return (body.response as unknown[])
    .map((raw) => {
      const r = raw as PermissionCaseDTO;
      const caseName = typeof r.name === 'string' ? r.name : '';
      const caseTag = typeof r.tag === 'string' ? r.tag.trim() : '';
      const folderSegment =
        typeof r.folder_segment === 'string' ? r.folder_segment.trim() : '';
      const csam = parseCaseCsamFlag(r);
      return {
        caseName,
        caseTag,
        folderSegment,
        csamFlagged: csam.csamFlagged,
        csamFlagSource: csam.csamFlagSource,
        csamCanTurnOff: csam.csamCanTurnOff,
      };
    })
    .filter((c) => c.caseTag !== '' && c.folderSegment !== '');
}

/**
 * A case as the create endpoint returns it (TR-4384).
 *
 * ``folderSegment`` is the unified-bucket folder name and must be used
 * **verbatim** by anything that writes objects for this case — never re-derived
 * from ``name``. For a token-bearing case the segment is a UUID that bears no
 * relation to the display name, and for a token-less one the two can differ by
 * interior whitespace. ``tag`` is the matching ``case:<segment>`` that ingested
 * rows carry, so it is what filters provenance.
 *
 * ``created`` is false when the call resolved to an existing case — a double
 * submit, or a case the folder already had. Treat it as success: there is one
 * case either way, and creating a second folder would split it.
 */
export interface CreatedCase {
  id: string;
  name: string;
  status: string;
  folderToken: string | null;
  folderSegment: string;
  tag: string;
  accessibleToAll: boolean;
  isPersonal: boolean;
  ownerId: string | null;
  created: boolean;
}

export class PermissionServiceError extends Error {
  readonly status: number;

  constructor(status: number, message: string) {
    super(message);
    this.name = 'PermissionServiceError';
    this.status = status;
  }
}

export interface CreateCaseArgs {
  /** Display name. Omit entirely when ``personal`` is set. */
  name?: string;
  /**
   * The caller's own "My Files" case. permission-service derives the name from
   * the caller's stored user record and **ignores any name sent** — otherwise a
   * client could name, and therefore own, another user's personal case.
   */
  personal?: boolean;
  /**
   * Ignored. ``POST /cases`` never pins the folder to the name. HelioTrace
   * pins its own UUID through ``POST /heliotrace/create-case``, which requires
   * that id to be a UUID. Accepting the pin here would put a display name,
   * including another user's email, into the S3 key.
   */
  useNameAsFolderSegment?: boolean;
  /**
   * TR-4241's "Open to all" choice. Honoured only when case-access enforcement
   * is active for the tenant (GATED, or the tenant-wide switch off); ignored
   * outright for a personal case, which is never shared.
   */
  openToAll?: boolean;
  signal?: AbortSignal;
}

/**
 * Create (or get) a case via edge-lambda ``POST /cases``.
 *
 * That route calls permission-service for the case row and writes
 * ``data/bulk/<folder_segment>/Manual/`` using the ``folder_segment``
 * permission-service returns. The Next proxy ``/api/protected/cases/create``
 * only creates the row, so the Create Case button must not use it.
 *
 * Idempotent on ``(tenant, name)``, so a retry or double submit returns the
 * existing case with ``created: false`` rather than a second case or a conflict.
 * Callers should surface the returned case either way.
 */
export async function createCase(args: CreateCaseArgs = {}): Promise<CreatedCase> {
  const { signal, name, personal, openToAll } = args;
  const session = await getGatewaySession();
  if (!session) {
    throw new Error('No authenticated session. Please refresh and try again.');
  }

  let dto: Record<string, unknown> | null;
  try {
    dto = await createUserCase({
      cellId: session.cellId,
      accessToken: session.accessToken,
      signal,
      name,
      personal,
      openToAll,
    });
  } catch (error) {
    if (error instanceof EdgeLambdaApiError) {
      const detail = error.detail ?? '';
      if (isWafBlockBody(error.status, detail)) {
        throw new WafBlockedError(
          'permission-service cases/create was blocked by the edge WAF.',
          detail,
        );
      }
      const status = error.status ?? 500;
      throw new PermissionServiceError(
        status,
        `permission-service cases/create returned ${status}${detail ? `: ${detail}` : ''}`,
      );
    }
    throw error;
  }
  // A mis-shapen payload is an error, not a silent partial: the caller is about
  // to key uploads off folderSegment, and a missing one would send them to the
  // wrong prefix (or to `undefined`).
  if (!dto || typeof dto.id !== 'string' || typeof dto.folder_segment !== 'string') {
    throw new Error('permission-service cases/create returned an unexpected payload');
  }

  return {
    id: dto.id,
    name: typeof dto.name === 'string' ? dto.name : '',
    status: typeof dto.status === 'string' ? dto.status : 'active',
    folderToken: typeof dto.folder_token === 'string' ? dto.folder_token : null,
    folderSegment: dto.folder_segment,
    tag: typeof dto.tag === 'string' ? dto.tag : '',
    accessibleToAll: dto.accessible_to_all === true,
    isPersonal: dto.is_personal === true,
    ownerId: typeof dto.owner_id === 'string' ? dto.owner_id : null,
    created: dto.created === true,
  };
}

export type CaseCsamFlagUpdate = CaseCsamFlag & {
  caseId: string;
  changed: boolean;
};

/**
 * Verifier-only sticky case CSAM flag (TR-2631). Proxied as
 * ``PUT /api/protected/cases/{caseId}/csam-flag`` with ``forceUserScope`` —
 * staff/CSM ``is_internal`` callers are 403 on the permission-service route.
 */
export async function setCaseCsamFlag(
  caseId: string,
  flagged: boolean,
  args: { signal?: AbortSignal } = {},
): Promise<CaseCsamFlagUpdate> {
  const res = await fetch(`/api/protected/cases/${encodeURIComponent(caseId)}/csam-flag`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ flagged }),
    signal: args.signal,
  });

  const detail = await res.text().catch(() => '');
  if (!res.ok) {
    if (isWafBlockBody(res.status, detail)) {
      throw new WafBlockedError(
        'permission-service cases/csam-flag was blocked by the edge WAF.',
        detail,
      );
    }
    throw new PermissionServiceError(
      res.status,
      `permission-service cases/csam-flag returned ${res.status}${detail ? `: ${detail}` : ''}`,
    );
  }

  const body = (JSON.parse(detail || 'null') as { response?: unknown } | null) ?? null;
  const dto =
    body && typeof body === 'object' && body.response && typeof body.response === 'object'
      ? (body.response as Record<string, unknown>)
      : null;
  if (!dto || typeof dto.case_id !== 'string') {
    throw new Error('permission-service cases/csam-flag returned an unexpected payload');
  }

  const csam = parseCaseCsamFlag(dto);
  return {
    caseId: dto.case_id,
    changed: dto.changed === true,
    ...csam,
  };
}
